import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Clock, Plus, User, FileText } from 'lucide-react';
import { format, set, addHours } from 'date-fns';
import { Employee, DayShift, HourRange, BusinessOpeningHour } from '@/types';
import { ShiftTemplate } from '@/components/shifts/ShiftTemplateItem';
import { toast } from '@/hooks/ui/use-toast';
import { useEmployeeAvailability } from '@/hooks/employees/useEmployeeAvailability';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { createBusinessTimezoneDate } from '@/lib/core/timezone-utils';

interface QuickShiftCreatorProps {
  selectedDate: Date;
  employees: Employee[];
  templates: ShiftTemplate[];
  recentTemplates?: ShiftTemplate[];
  onCreateShift: (businessId: string, startTime: Date, endTime: Date, title: string, employeeId?: string) => Promise<void>;
  onTemplateUsed?: (template: ShiftTemplate) => void;
  businessId: string;
  openingHours?: HourRange[];
  businessHours?: BusinessOpeningHour[] | null;
  allDayShifts?: DayShift[];
  businessTimezone?: string; // Add timezone prop
  businessCountryCode?: string; // Add country code for timezone fallback
}

const QuickShiftCreator: React.FC<QuickShiftCreatorProps> = ({
  selectedDate,
  employees,
  templates,
  recentTemplates = [],
  onCreateShift,
  onTemplateUsed,
  businessId,
  openingHours = [],
  businessHours,
  allDayShifts = [],
  businessTimezone,
  businessCountryCode
}) => {
  // Determine business timezone - use provided timezone or browser timezone (no country fallback)
  const resolvedTimezone = businessTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

  const [startTimeDate, setStartTimeDate] = useState<Date>(() => {
    // Default to 9:00 AM on the selected date using business timezone
    try {
      return createBusinessTimezoneDate(selectedDate, 9, 0, resolvedTimezone);
    } catch (error) {
      // Fallback to browser timezone if timezone conversion fails
      return set(selectedDate, { hours: 9, minutes: 0, seconds: 0, milliseconds: 0 });
    }
  });
  const [duration, setDuration] = useState('8');
  const [selectedEmployee, setSelectedEmployee] = useState<string>('assign_later');
  const [selectedTemplate, setSelectedTemplate] = useState<string>('none');
  const [customTitle, setCustomTitle] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  // Calculate shift times for availability checking
  const proposedStartTime = startTimeDate;
  
  const proposedEndTime = proposedStartTime && duration ? 
    addHours(proposedStartTime, parseFloat(duration)) : null;

  // Use the availability hook
  const { isEmployeeAvailable, getEmployeeConflicts } = useEmployeeAvailability({
    employees,
    proposedStartTime,
    proposedEndTime,
    existingShifts: allDayShifts,
    excludeShiftId: undefined
  });

  // Shift start times are independent of business opening hours
  // Users should be able to create shifts at any time, including outside business hours

  // Update duration and title when template is selected
  useEffect(() => {
    if (selectedTemplate && selectedTemplate !== 'none') {
      const template = [...templates, ...recentTemplates].find(t => t.id === selectedTemplate);
      if (template) {
        setDuration((template.duration / 60).toString());
        setCustomTitle(template.name);
        // Track template usage
        if (onTemplateUsed) {
          onTemplateUsed(template);
        }
      }
    }
  }, [selectedTemplate, templates, recentTemplates, onTemplateUsed]);

  const handleCreateShift = async () => {
    if (!startTimeDate || !duration) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsCreating(true);
    try {
      const endDateTime = addHours(startTimeDate, parseFloat(duration));
      
      const title = customTitle || selectedTemplate ? 
        templates.find(t => t.id === selectedTemplate)?.name || 'Shift' : 
        'Shift';

      await onCreateShift(businessId, startTimeDate, endDateTime, title, selectedEmployee === 'assign_later' ? undefined : selectedEmployee || undefined);
      
      // Reset form
      setSelectedEmployee('assign_later');
      setSelectedTemplate('none');
      setCustomTitle('');
      try {
        setStartTimeDate(createBusinessTimezoneDate(selectedDate, 9, 0, resolvedTimezone));
      } catch (error) {
        // Fallback to browser timezone if timezone conversion fails
        setStartTimeDate(set(selectedDate, { hours: 9, minutes: 0, seconds: 0, milliseconds: 0 }));
      }
      toast.success('Shift created successfully!');
    } catch (error) {
      toast.error('Failed to create shift');
    } finally {
      setIsCreating(false);
    }
  };

  const commonDurations = [
    { value: '4', label: '4 hours' },
    { value: '6', label: '6 hours' },
    { value: '8', label: '8 hours' },
    { value: '10', label: '10 hours' },
    { value: '12', label: '12 hours' }
  ];

  return (
    <div className="flex flex-col space-y-4">

          {/* Recent Templates Quick Actions */}
          {recentTemplates.length > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-1">
                <FileText className="h-3 w-3" />
                Recently Used Templates
              </h4>
              <div className="flex flex-wrap gap-2">
                {recentTemplates.slice(0, 4).map((template) => (
                  <Button
                    key={template.id}
                    variant="outline"
                    size="sm"
                    className="h-8 text-xs"
                    onClick={() => {
                      setSelectedTemplate(template.id);
                      setDuration((template.duration / 60).toString());
                      setCustomTitle(template.name);
                      if (onTemplateUsed) {
                        onTemplateUsed(template);
                      }
                    }}
                  >
                    {template.name} ({Math.floor(template.duration / 60)}h)
                  </Button>
                ))}
              </div>
            </div>
          )}
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
            {/* Start Time */}
            <div className="space-y-2">
              <Label htmlFor="start-time" className="text-sm font-medium flex items-center gap-1">
                <Clock className="h-3 w-3" />
                Start Time
              </Label>
              <Input
                id="start-time"
                type="time"
                value={format(startTimeDate, 'HH:mm')}
                onChange={(e) => {
                  const [hours, minutes] = e.target.value.split(':').map(Number);
                  try {
                    // Create timezone-aware date from user input
                    const timezoneAwareDate = createBusinessTimezoneDate(selectedDate, hours, minutes, resolvedTimezone);
                    setStartTimeDate(timezoneAwareDate);
                  } catch (error) {
                    // Fallback to browser timezone if timezone conversion fails
                    console.warn('Timezone conversion failed, using browser timezone:', error);
                    setStartTimeDate(set(selectedDate, { hours, minutes, seconds: 0, milliseconds: 0 }));
                  }
                }}
              />
            </div>

            {/* Duration */}
            <div className="space-y-2">
              <Label htmlFor="duration" className="text-sm font-medium">Duration</Label>
              <Select value={duration} onValueChange={setDuration}>
                <SelectTrigger>
                  <SelectValue placeholder="Select duration" />
                </SelectTrigger>
                <SelectContent>
                  {commonDurations.map((dur) => (
                    <SelectItem key={dur.value} value={dur.value}>
                      {dur.label}
                    </SelectItem>
                  ))}
                  <SelectItem value="custom">Custom</SelectItem>
                </SelectContent>
              </Select>
              {duration === 'custom' && (
                <Input
                  type="number"
                  step="0.5"
                  min="0.5"
                  max="24"
                  placeholder="Hours"
                  onChange={(e) => setDuration(e.target.value)}
                  className="mt-2"
                />
              )}
            </div>

            {/* Employee */}
            <div className="space-y-2">
              <Label htmlFor="employee" className="text-sm font-medium flex items-center gap-1">
                <User className="h-3 w-3" />
                Employee
              </Label>
              <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
                <SelectTrigger>
                  <SelectValue placeholder="Assign later" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="assign_later">Assign later</SelectItem>
                  {employees.map((employee) => {
                    const available = isEmployeeAvailable(employee.id);
                    const conflicts = getEmployeeConflicts(employee.id);
                    
                    return (
                      <SelectItem
                        key={employee.id}
                        value={employee.id}
                        className={available ? "" : "text-red-600"}
                      >
                        {/* Note: TooltipProvider is hoisted to ComposedShiftProvider */}
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="flex items-center gap-2 w-full">
                              <div className={`w-2 h-2 ${available ? 'bg-green-500' : 'bg-red-500'} rounded-full`}></div>
                              {employee.name}
                            </span>
                          </TooltipTrigger>
                          {!available && conflicts && conflicts.length > 0 && (
                            <TooltipContent>
                              <p className="text-sm">Conflicts with:</p>
                              {conflicts.map((conflict, idx) => (
                                <p key={idx} className="text-xs">
                                  {conflict.title || 'Shift'} ({format(conflict.startTime, 'h:mm a')} - {format(conflict.endTime, 'h:mm a')})
                                </p>
                              ))}
                            </TooltipContent>
                          )}
                        </Tooltip>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            {/* Template */}
            <div className="space-y-2">
              <Label htmlFor="template" className="text-sm font-medium flex items-center gap-1">
                <FileText className="h-3 w-3" />
                Template
              </Label>
              <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
                <SelectTrigger>
                  <SelectValue placeholder="None" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {templates.map((template) => (
                    <SelectItem key={template.id} value={template.id}>
                      {template.name} ({Math.floor(template.duration / 60)}h)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Create Button */}
            <Button 
              onClick={handleCreateShift}
              disabled={isCreating || !startTime || !duration}
              className="px-6 py-2 h-10"
            >
              {isCreating ? 'Creating...' : 'Create Shift'}
            </Button>
          </div>

          {/* Custom Title (appears when template is selected) */}
          {selectedTemplate && (
            <div className="mt-2">
              <Label htmlFor="custom-title" className="text-sm font-medium">Shift Title</Label>
              <Input
                id="custom-title"
                value={customTitle}
                onChange={(e) => setCustomTitle(e.target.value)}
                placeholder="Enter custom title"
                className="mt-1"
              />
            </div>
          )}

    </div>
  );
};

export default QuickShiftCreator;